// src/components/ProxyUser.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import OperatorDashboard from './OperatorDashboard';
import ClientDashboard from './ClientDashboard';
import AdminDashboard from './AdminDashboard';
import { getUserRole } from '../utils/rolePermissions';
import { useOrgBrand, OrgBrandProvider } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import '../styles/ProxyUser.css';
import CustomSelect from './CustomSelect';

// Component that renders the proxied dashboard with its own OrgBrandContext
const ProxiedDashboardContent = ({ activeTab, selectedUser, onLogout }) => {
  const { reinitializePreferences } = useOrgBrand();

  // Initialize with proxied user's preferences when this component mounts
  useEffect(() => {
    reinitializePreferences();
  }, [reinitializePreferences]);

  if (activeTab === 'client') {
    return <ClientDashboard user={selectedUser} onLogout={onLogout} isProxyMode={true} />;
  } else if (activeTab === 'operator') {
    return <OperatorDashboard user={selectedUser} onLogout={onLogout} isProxyMode={true} />;
  } else {
    return <AdminDashboard user={selectedUser} onLogout={onLogout} isProxyMode={true} />;
  }
};

const ProxyUser = ({ user, onLogout, dashboardData }) => {
  const modal = useModal();
  const isSuperAdmin = getUserRole(user) === 'super_admin';
  // Note: We don't need to access OrgBrandContext here since proxied dashboard has its own context
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('proxy_user_active_tab') || 'client';
  });

  useEffect(() => {
    localStorage.setItem('proxy_user_active_tab', activeTab);
  }, [activeTab]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [proxiedUserData, setProxiedUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Helper function to safely get string values
  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  // Get clients for proxy (sorted alpha-numerically by name)
  const getClients = () => {
    const clients = dashboardData?.data?.clients || [];
    return [...clients].sort((a, b) => {
      const nameA = safeString(a.name).toLowerCase();
      const nameB = safeString(b.name).toLowerCase();
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  // Get operators for proxy (sorted alpha-numerically by name)
  const getOperators = () => {
    const operators = dashboardData?.data?.operators || [];
    return [...operators].sort((a, b) => {
      const nameA = safeString(a.name).toLowerCase();
      const nameB = safeString(b.name).toLowerCase();
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  // Get admins for proxy (super admin only, sorted alpha-numerically by name)
  const getAdmins = () => {
    const admins = dashboardData?.data?.admins || [];
    return [...admins].sort((a, b) => {
      const nameA = safeString(a.name).toLowerCase();
      const nameB = safeString(b.name).toLowerCase();
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  // Handle user selection
  const handleUserSelect = async (userId) => {
    if (!userId) {
      await clearProxy();
      return;
    }

    setLoading(true);
    setSelectedUserId(userId);

    try {
      // Note: No need to save/clear admin state since proxied dashboard has its own OrgBrandContext
      // The admin's org/brand selection remains untouched in the outer context

      // Find the user from the dashboard data
      let users;
      if (activeTab === 'client') {
        users = getClients();
      } else if (activeTab === 'operator') {
        users = getOperators();
      } else {
        users = getAdmins();
      }
      const foundUser = users.find(u => safeString(u._id) === userId);

      if (foundUser) {
        setSelectedUser(foundUser);

        // Collapse the admin's left nav when proxying into a user
        localStorage.setItem('adminSidebarCollapsed', JSON.stringify(true));
        // Dispatch event to trigger collapse
        window.dispatchEvent(new CustomEvent('collapseAdminNav'));

        // For admin tab, we don't need to assign org/brand role
        // For ALL user types (admin, operator, client), just set the proxied user ID
        // The backend will handle permissions based on the proxied user's actual role
        await axios.post('/api/super-admin/assign-self-role', {
          user_id: userId  // Only this is needed - backend ignores role and entity_id
        });

        // Fetch the user data to populate the proxied view
        setProxiedUserData(foundUser);
      }
    } catch (error) {
      console.error('Error proxying as user:', error);
      modal.error('Failed to proxy as user: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Clear proxy mode
  const clearProxy = async () => {
    // Don't do anything if there's no proxied user
    if (!selectedUser && !selectedUserId) {
      return;
    }

    // Set transitioning flag to prevent dashboard from rendering during cleanup
    setIsTransitioning(true);

    // Immediately clear UI state to prevent rendering issues during transition
    setSelectedUserId('');
    setSelectedUser(null);
    setProxiedUserData(null);

    try {
      await axios.post('/api/super-admin/clear-role-assignments');

      // Note: No need to clear/restore admin's org/brand state
      // The admin's context remains untouched, only the inner proxied context is destroyed
    } catch (error) {
      console.error('Error clearing proxy:', error);
      modal.error('Failed to clear proxy mode');
    } finally {
      // Small delay to ensure old dashboard fully unmounts before new one mounts
      setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
    }
  };

  // Reset when tab changes (only if a user is actually proxied)
  useEffect(() => {
    if (selectedUser || selectedUserId) {
      clearProxy();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Clear proxy from session when the super admin navigates away from this panel
  useEffect(() => {
    return () => {
      axios.post('/api/super-admin/clear-role-assignments', {}, { withCredentials: true })
        .catch(() => {});
    };
  }, []);

  // Add beforeunload listener to clear proxy when browser closes
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Synchronous cleanup before page unloads
      const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
      navigator.sendBeacon('/api/super-admin/clear-role-assignments', blob);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return (
    <div className="proxy-user-container">
      {/* Horizontal control bar */}
      <nav className="internal-nav">
        <div className="internal-nav-items">
          <button
            className={`internal-nav-btn ${activeTab === 'client' ? 'active' : ''}`}
            onClick={() => setActiveTab('client')}
            data-onboarding="clients-tab"
          >
            Clients
          </button>
          <button
            className={`internal-nav-btn ${activeTab === 'operator' ? 'active' : ''}`}
            onClick={() => setActiveTab('operator')}
            data-onboarding="operators-tab"
          >
            Operators
          </button>
          {isSuperAdmin && (
            <button
              className={`internal-nav-btn ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => setActiveTab('admin')}
              data-onboarding="admins-tab"
            >
              Admins
            </button>
          )}
        </div>

        <div className="proxy-selection">
          <CustomSelect
              id="user-select"
              value={selectedUserId}
              onChange={(e) => handleUserSelect(e.target.value)}
              disabled={loading}
            >
              <option value="">-- Select {activeTab === 'client' ? 'Client' : activeTab === 'operator' ? 'Operator' : 'Admin'} --</option>
              {activeTab === 'client' && getClients().map(client => (
                <option key={safeString(client._id)} value={safeString(client._id)}>
                  {safeString(client.name)} ({safeString(client.email)})
                  {client.organization_name && ` - ${safeString(client.organization_name)}`}
                  {client.brand_name && ` / ${safeString(client.brand_name)}`}
                </option>
              ))}
              {activeTab === 'operator' && getOperators().map(manager => (
                <option key={safeString(manager._id)} value={safeString(manager._id)}>
                  {safeString(manager.name)} ({safeString(manager.email)})
                </option>
              ))}
              {activeTab === 'admin' && getAdmins().map(admin => (
                <option key={safeString(admin._id)} value={safeString(admin._id)}>
                  {safeString(admin.name)} ({safeString(admin.email)})
                  {admin.agency_name && ` - ${safeString(admin.agency_name)}`}
                </option>
              ))}
            </CustomSelect>
          {selectedUser && (
            <button className="clear-proxy-btn" onClick={clearProxy}>
              Exit Proxy
            </button>
          )}
        </div>
      </nav>

      {/* Proxied User Dashboard */}
      {(loading || isTransitioning) && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading user view...</p>
        </div>
      )}

      {!loading && !isTransitioning && selectedUser && (
        <div className="proxied-dashboard">
          <div className="dashboard-wrapper">
            {/* Wrap in separate OrgBrandProvider so proxied user has their own org/brand context */}
            <OrgBrandProvider>
              <ProxiedDashboardContent
                activeTab={activeTab}
                selectedUser={selectedUser}
                onLogout={onLogout}
              />
            </OrgBrandProvider>
          </div>
        </div>
      )}

      {!loading && !isTransitioning && !selectedUser && (
        <div className="empty-proxy-state">
          <p>Select a {activeTab === 'client' ? 'client' : activeTab === 'operator' ? 'operator' : 'admin'} from the dropdown above to view their dashboard</p>
        </div>
      )}
    </div>
  );
};

export default ProxyUser;
